import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AnalyticsService, AnalyticsSummary } from '../../shared/services/analytics';

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  template: `
    <main class="max-w-7xl mx-auto px-6 py-24 sm:py-32">
      <!-- Header & Controls -->
      <header class="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-stone-200 pb-8 mb-12">
        <div>
          <div class="flex items-center gap-3 mb-2">
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
              <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Internal Analytics Engine Active
            </span>
            <span class="text-xs text-stone-500 font-mono">100% First-Party Data</span>
          </div>
          <h1 class="font-serif text-4xl text-on-surface">Website Traffic & Analytics</h1>
          <p class="text-stone-600 mt-1">Real-time visitor logs, pageview metrics, traffic referrers, and device diagnostics.</p>
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <!-- Timeframe Selector -->
          <div class="bg-stone-100 p-1 rounded-lg border border-stone-200 flex text-xs font-bold">
            <button 
              (click)="setTimeframe('24h')" 
              [class]="selectedTimeframe() === '24h' ? 'bg-white shadow text-on-surface px-3 py-2 rounded' : 'text-stone-600 px-3 py-2 hover:text-on-surface'">
              24 Hours
            </button>
            <button 
              (click)="setTimeframe('7d')" 
              [class]="selectedTimeframe() === '7d' ? 'bg-white shadow text-on-surface px-3 py-2 rounded' : 'text-stone-600 px-3 py-2 hover:text-on-surface'">
              7 Days
            </button>
            <button 
              (click)="setTimeframe('30d')" 
              [class]="selectedTimeframe() === '30d' ? 'bg-white shadow text-on-surface px-3 py-2 rounded' : 'text-stone-600 px-3 py-2 hover:text-on-surface'">
              30 Days
            </button>
            <button 
              (click)="setTimeframe('all')" 
              [class]="selectedTimeframe() === 'all' ? 'bg-white shadow text-on-surface px-3 py-2 rounded' : 'text-stone-600 px-3 py-2 hover:text-on-surface'">
              All Time
            </button>
          </div>

          <button 
            (click)="loadData()" 
            class="inline-flex items-center gap-2 border border-stone-300 hover:bg-stone-50 text-stone-700 font-bold px-4 py-2.5 rounded-lg text-xs transition-all">
            <span class="material-symbols-outlined text-sm" [class.animate-spin]="isLoading()">refresh</span>
            Refresh
          </button>

          <button 
            (click)="onClearLogs()" 
            class="inline-flex items-center gap-2 border border-red-200 text-red-700 hover:bg-red-50 font-bold px-4 py-2.5 rounded-lg text-xs transition-all">
            <span class="material-symbols-outlined text-sm">delete_sweep</span>
            Reset Data
          </button>
        </div>
      </header>

      @if (error()) {
        <div class="mb-8 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
          <span class="material-symbols-outlined text-xl">error</span>
          <span>{{ error() }}</span>
        </div>
      }

      @if (isLoading() && !summary()) {
        <div class="py-24 text-center">
          <span class="material-symbols-outlined text-4xl text-stone-400 animate-spin mb-4">sync</span>
          <p class="text-stone-500 font-mono text-sm">Loading internal analytics metrics...</p>
        </div>
      } @else if (summary()) {
        <!-- Metric Overview Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <!-- Card 1: Total Pageviews -->
          <div class="bg-white border-2 border-stone-100 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start mb-4">
              <span class="text-xs font-bold uppercase tracking-widest text-stone-500">Total Pageviews</span>
              <span class="w-10 h-10 rounded-lg bg-amber-50 text-gold-accent flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">visibility</span>
              </span>
            </div>
            <p class="text-4xl font-serif font-bold text-on-surface mb-1">{{ summary()?.totalPageviews | number }}</p>
            <p class="text-xs text-stone-500">Total page requests recorded</p>
          </div>

          <!-- Card 2: Unique Visitors -->
          <div class="bg-white border-2 border-stone-100 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start mb-4">
              <span class="text-xs font-bold uppercase tracking-widest text-stone-500">Unique Visitors</span>
              <span class="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">group</span>
              </span>
            </div>
            <p class="text-4xl font-serif font-bold text-on-surface mb-1">{{ summary()?.uniqueVisitors | number }}</p>
            <p class="text-xs text-stone-500">Anonymized unique visitor hash</p>
          </div>

          <!-- Card 3: Avg Pages per Visit -->
          <div class="bg-white border-2 border-stone-100 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start mb-4">
              <span class="text-xs font-bold uppercase tracking-widest text-stone-500">Pages / Visitor</span>
              <span class="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">conversion_path</span>
              </span>
            </div>
            <p class="text-4xl font-serif font-bold text-on-surface mb-1">{{ summary()?.avgPageviewsPerVisitor }}</p>
            <p class="text-xs text-stone-500">Average page depth per session</p>
          </div>

          <!-- Card 4: Total Bookings -->
          <div class="bg-white border-2 border-stone-100 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start mb-4">
              <span class="text-xs font-bold uppercase tracking-widest text-stone-500">Conversions & Bookings</span>
              <span class="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">event_available</span>
              </span>
            </div>
            <p class="text-4xl font-serif font-bold text-on-surface mb-1">{{ summary()?.totalBookings | number }}</p>
            <p class="text-xs text-stone-500">Scheduled services completed</p>
          </div>
        </div>

        <!-- Daily Traffic Chart Section -->
        @if (summary()?.dailyTrends && (summary()?.dailyTrends)!.length > 0) {
          <section class="bg-white border-2 border-stone-100 p-8 rounded-xl shadow-sm mb-12">
            <div class="flex justify-between items-center mb-8">
              <div>
                <h2 class="font-serif text-2xl text-on-surface">Traffic Volume Trend</h2>
                <p class="text-xs text-stone-500">Daily breakdown of pageviews and unique visitors</p>
              </div>
              <div class="flex items-center gap-4 text-xs font-bold">
                <span class="flex items-center gap-1.5"><span class="w-3 h-3 rounded bg-amber-500"></span> Pageviews</span>
                <span class="flex items-center gap-1.5"><span class="w-3 h-3 rounded bg-stone-700"></span> Unique Visitors</span>
              </div>
            </div>

            <!-- Bar Chart -->
            <div class="h-64 flex items-end gap-3 pt-6 pb-2 border-b border-stone-200 overflow-x-auto">
              @for (day of summary()?.dailyTrends; track day.date) {
                <div class="flex-1 min-w-[40px] flex flex-col items-center gap-1 group relative">
                  <!-- Tooltip -->
                  <div class="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-stone-900 text-white text-xs p-2 rounded shadow-lg pointer-events-none z-10 whitespace-nowrap">
                    <p class="font-bold">{{ day.date }}</p>
                    <p>{{ day.pageviews }} Views</p>
                    <p>{{ day.visitors }} Visitors</p>
                  </div>

                  <div class="w-full flex items-end justify-center gap-1 h-48">
                    <!-- Pageviews Bar -->
                    <div 
                      class="w-1/2 bg-amber-500 rounded-t group-hover:bg-amber-400 transition-all"
                      [style.height.%]="getMaxTrendHeight(day.pageviews)">
                    </div>
                    <!-- Visitors Bar -->
                    <div 
                      class="w-1/2 bg-stone-700 rounded-t group-hover:bg-stone-600 transition-all"
                      [style.height.%]="getMaxTrendHeight(day.visitors)">
                    </div>
                  </div>
                  <span class="text-[10px] font-mono text-stone-500 truncate w-full text-center">{{ day.date | date:'MM/dd' }}</span>
                </div>
              }
            </div>
          </section>
        }

        <!-- Breakdown Grid: Top Pages & Top Referrers -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <!-- Top Pages -->
          <section class="bg-white border-2 border-stone-100 p-8 rounded-xl shadow-sm">
            <h2 class="font-serif text-2xl text-on-surface mb-6 flex items-center gap-2">
              <span class="material-symbols-outlined text-amber-600">file_open</span>
              Top Viewed Pages
            </h2>

            <div class="space-y-4">
              @for (page of summary()?.topPages; track page.path) {
                <div>
                  <div class="flex justify-between items-center text-sm mb-1.5">
                    <span class="font-mono text-stone-800 font-medium truncate max-w-[240px] sm:max-w-xs">{{ page.path }}</span>
                    <span class="text-xs text-stone-500 font-bold">{{ page.views }} views ({{ page.percentage }}%)</span>
                  </div>
                  <div class="w-full bg-stone-100 h-2.5 rounded-full overflow-hidden">
                    <div class="bg-amber-500 h-full rounded-full" [style.width.%]="page.percentage"></div>
                  </div>
                </div>
              } @empty {
                <p class="text-stone-400 italic text-sm">No pageview records captured yet.</p>
              }
            </div>
          </section>

          <!-- Top Referrers -->
          <section class="bg-white border-2 border-stone-100 p-8 rounded-xl shadow-sm">
            <h2 class="font-serif text-2xl text-on-surface mb-6 flex items-center gap-2">
              <span class="material-symbols-outlined text-blue-600">link</span>
              Traffic Sources & Referrers
            </h2>

            <div class="space-y-4">
              @for (ref of summary()?.topReferrers; track ref.referrer) {
                <div>
                  <div class="flex justify-between items-center text-sm mb-1.5">
                    <span class="font-medium text-stone-800">{{ ref.referrer }}</span>
                    <span class="text-xs text-stone-500 font-bold">{{ ref.count }} visits ({{ ref.percentage }}%)</span>
                  </div>
                  <div class="w-full bg-stone-100 h-2.5 rounded-full overflow-hidden">
                    <div class="bg-blue-600 h-full rounded-full" [style.width.%]="ref.percentage"></div>
                  </div>
                </div>
              } @empty {
                <p class="text-stone-400 italic text-sm">No referrer logs registered yet.</p>
              }
            </div>
          </section>
        </div>

        <!-- Devices, Operating Systems & Browsers -->
        <section class="bg-white border-2 border-stone-100 p-8 rounded-xl shadow-sm mb-12">
          <h2 class="font-serif text-2xl text-on-surface mb-8 flex items-center gap-2">
            <span class="material-symbols-outlined text-purple-600">devices</span>
            Device & Browser Diagnostics
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <!-- Devices -->
            <div>
              <h3 class="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4">Device Type</h3>
              <div class="space-y-3">
                @for (d of summary()?.deviceBreakdown; track d.device) {
                  <div>
                    <div class="flex justify-between text-xs mb-1 font-bold">
                      <span>{{ d.device }}</span>
                      <span>{{ d.percentage }}%</span>
                    </div>
                    <div class="w-full bg-stone-100 h-2 rounded-full overflow-hidden">
                      <div class="bg-purple-600 h-full" [style.width.%]="d.percentage"></div>
                    </div>
                  </div>
                }
              </div>
            </div>

            <!-- Browsers -->
            <div>
              <h3 class="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4">Browser</h3>
              <div class="space-y-3">
                @for (b of summary()?.browserBreakdown; track b.browser) {
                  <div>
                    <div class="flex justify-between text-xs mb-1 font-bold">
                      <span>{{ b.browser }}</span>
                      <span>{{ b.percentage }}%</span>
                    </div>
                    <div class="w-full bg-stone-100 h-2 rounded-full overflow-hidden">
                      <div class="bg-indigo-600 h-full" [style.width.%]="b.percentage"></div>
                    </div>
                  </div>
                }
              </div>
            </div>

            <!-- OS -->
            <div>
              <h3 class="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4">Operating System</h3>
              <div class="space-y-3">
                @for (os of summary()?.osBreakdown; track os.os) {
                  <div>
                    <div class="flex justify-between text-xs mb-1 font-bold">
                      <span>{{ os.os }}</span>
                      <span>{{ os.percentage }}%</span>
                    </div>
                    <div class="w-full bg-stone-100 h-2 rounded-full overflow-hidden">
                      <div class="bg-emerald-600 h-full" [style.width.%]="os.percentage"></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </section>

        <!-- Real-Time Activity Log Table -->
        <section class="bg-white border-2 border-stone-100 rounded-xl shadow-sm overflow-hidden">
          <div class="p-8 border-b border-stone-200 flex justify-between items-center">
            <div>
              <h2 class="font-serif text-2xl text-on-surface">Recent Visitor Activity Stream</h2>
              <p class="text-xs text-stone-500">Live incoming HTTP request and interaction stream</p>
            </div>
            <span class="text-xs font-mono bg-stone-100 px-3 py-1.5 rounded-full text-stone-600">
              Showing latest {{ summary()?.recentLogs?.length }} logs
            </span>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse">
              <thead>
                <tr class="bg-stone-50 border-b border-stone-200 text-[11px] font-bold tracking-widest text-stone-500 uppercase">
                  <th class="py-4 px-6">Timestamp</th>
                  <th class="py-4 px-6">Visitor ID</th>
                  <th class="py-4 px-6">Page Path</th>
                  <th class="py-4 px-6">Referrer</th>
                  <th class="py-4 px-6">Device / Browser</th>
                  <th class="py-4 px-6">Event Type</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-stone-100 text-xs font-mono">
                @for (log of summary()?.recentLogs; track log.id) {
                  <tr class="hover:bg-stone-50 transition-colors">
                    <td class="py-3.5 px-6 text-stone-600 whitespace-nowrap">
                      {{ log.timestamp | date:'MMM d, HH:mm:ss' }}
                    </td>
                    <td class="py-3.5 px-6 font-bold text-stone-800">
                      <span class="bg-stone-100 px-2 py-1 rounded">{{ log.visitorId }}</span>
                    </td>
                    <td class="py-3.5 px-6 text-stone-900 font-bold">
                      {{ log.path }}
                    </td>
                    <td class="py-3.5 px-6 text-stone-600">
                      {{ log.referrer }}
                    </td>
                    <td class="py-3.5 px-6 text-stone-700">
                      <span class="inline-flex items-center gap-1 bg-stone-100 text-stone-800 px-2 py-0.5 rounded text-[11px] font-sans">
                        {{ log.deviceType }} • {{ log.browser }} ({{ log.os }})
                      </span>
                    </td>
                    <td class="py-3.5 px-6">
                      <span [class]="log.eventType === 'conversion' ? 'bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-bold' : 'bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold'">
                        {{ log.eventType }}
                      </span>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="6" class="py-8 text-center text-stone-400 italic">No activity logs recorded yet.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      }
    </main>
  `
})
export class AnalyticsComponent implements OnInit {
  private readonly analyticsService = inject(AnalyticsService);

  readonly selectedTimeframe = signal<'24h' | '7d' | '30d' | 'all'>('7d');
  readonly summary = signal<AnalyticsSummary | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  ngOnInit(): void {
    this.loadData();
  }

  setTimeframe(tf: '24h' | '7d' | '30d' | 'all'): void {
    this.selectedTimeframe.set(tf);
    this.loadData();
  }

  loadData(): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.analyticsService.getSummary(this.selectedTimeframe()).subscribe({
      next: (res) => {
        this.summary.set(res);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('[AnalyticsComponent] Failed to load summary metrics:', err);
        this.error.set('Failed to load internal analytics data. Please check server status.');
        this.isLoading.set(false);
      }
    });
  }

  onClearLogs(): void {
    if (confirm('Are you sure you want to reset all internal analytics metrics and log data?')) {
      this.analyticsService.clearAnalytics().subscribe({
        next: () => {
          this.loadData();
        },
        error: (err) => {
          console.error('[AnalyticsComponent] Error clearing data:', err);
        }
      });
    }
  }

  getMaxTrendHeight(val: number): number {
    const trends = this.summary()?.dailyTrends || [];
    if (trends.length === 0) return 0;
    const maxVal = Math.max(...trends.map(t => Math.max(t.pageviews, t.visitors)), 1);
    return Math.max(Math.round((val / maxVal) * 100), 5);
  }
}
